import { LOAD_EXT_NOT_FOUND } from '../../constants'
import { PostHog } from '../../posthog-core'
import {
    ConversationsRemoteConfig,
    GetMessagesResponse,
    GetTicketsOptions,
    GetTicketsResponse,
    MarkAsReadResponse,
    RestoreFromTokenResponse,
    RequestRestoreLinkResponse,
    SendMessageResponse,
    UserProvidedTraits,
} from '../../posthog-conversations-types'
import { RemoteConfigResult } from '../../types'
import { assignableWindow, LazyLoadedConversationsInterface } from '../../utils/globals'
import { createLogger } from '@posthog/browser-common/utils/logger'
import { isNullish, isUndefined, isBoolean, isNull } from '@posthog/core'
import { isToolbarInstance } from '@posthog/browser-common/utils/general-utils'

import type { ConversationsUnavailableReason } from '../../posthog-conversations-types'

export type { ConversationsUnavailableReason }
import { Extension } from '../types'

const logger = createLogger('[Conversations]')
const NOT_AVAILABLE = 'Conversations not available yet.'

export type ConversationsManager = LazyLoadedConversationsInterface

export class PostHogConversations implements Extension {
    // This is set to undefined until the remote config is loaded
    // then it's set to true if conversations are enabled
    // or false if conversations are disabled in the project settings
    private _isConversationsEnabled?: boolean = undefined
    private _conversationsManager: LazyLoadedConversationsInterface | null = null
    private _isInitializing: boolean = false
    private _remoteConfig: ConversationsRemoteConfig | null = null
    private _remoteConfigSuccessful?: boolean
    // Set when the lazy bundle fails to load or init (blocked, network, CSP, init throw); cleared on
    // successful load. Distinguishes a load failure from "still initializing" in getUnavailableReason().
    // A boolean rather than the message: only the fact of failure is read, and this class lands in the
    // non-lazy bundle. The message is already logged at the point of failure.
    private _loadFailed: boolean = false

    constructor(private _instance: PostHog) {}

    initialize() {
        this.loadIfEnabled()
    }

    onRemoteConfig(result: RemoteConfigResult) {
        // Don't load conversations if disabled via config
        if (this._instance.config.disable_conversations) {
            return
        }

        this._remoteConfigSuccessful = result.ok
        if (!result.ok) {
            return
        }

        const conversations = result.config['conversations']
        if (isNullish(conversations)) {
            return
        }

        // Handle both boolean and object response
        if (isBoolean(conversations)) {
            this._isConversationsEnabled = conversations
        } else {
            // It's a ConversationsRemoteConfig object
            this._isConversationsEnabled = conversations.enabled
            this._remoteConfig = conversations
        }

        this.loadIfEnabled()
    }

    reset(): void {
        // Delegate cleanup to the lazy-loaded manager (which knows about persistence keys)
        // If not loaded, there's nothing to reset anyway
        this._conversationsManager?.reset()
        this._conversationsManager = null

        // Reset local state
        this._isConversationsEnabled = undefined
        this._remoteConfig = null
        this._remoteConfigSuccessful = undefined
        this._loadFailed = false
    }

    loadIfEnabled() {
        if (this._conversationsManager) {
            return
        }
        if (this._isInitializing) {
            return
        }
        if (this._instance.config.disable_conversations) {
            return
        }
        // The toolbar's internal PostHog instance must not own the conversations
        // manager — its distinct_id is always anonymous and would be sent instead
        // of the identified user's ID.
        if (isToolbarInstance(this._instance.config)) {
            return
        }
        if (this._instance.config.cookieless_mode && this._instance.consent.isOptedOut()) {
            return
        }

        const phExtensions = assignableWindow?.__PosthogExtensions__
        if (!phExtensions) {
            return
        }

        // Wait for remote config to load
        if (isUndefined(this._isConversationsEnabled)) {
            return
        }

        // Check if conversations are enabled
        if (!this._isConversationsEnabled) {
            return
        }

        // Check if we have the required config
        if (!this._remoteConfig || !this._remoteConfig.token) {
            logger.error('Conversations enabled but missing token in remote config.')
            return
        }

        // Note: Domain check is done in ConversationsManager for widget rendering
        // The conversations API is loaded regardless of domain restrictions

        this._isInitializing = true

        try {
            const initConversations = phExtensions.initConversations
            if (initConversations) {
                // Conversations code is already loaded
                this._completeInitialization(initConversations)
                this._isInitializing = false
                return
            }

            // If we reach here, conversations code is not loaded yet
            const loadExternalDependency = phExtensions.loadExternalDependency
            if (!loadExternalDependency) {
                this._handleLoadError(LOAD_EXT_NOT_FOUND)
                return
            }

            // Load the conversations bundle
            loadExternalDependency(this._instance, 'conversations', (err) => {
                if (err || !phExtensions.initConversations) {
                    this._handleLoadError('Could not load conversations script', err)
                } else {
                    this._completeInitialization(phExtensions.initConversations)
                }
                this._isInitializing = false
            })
        } catch (e) {
            this._handleLoadError('Error initializing conversations', e)
            this._isInitializing = false
        }
    }

    /** Helper to finalize conversations initialization */
    private _completeInitialization(
        initConversationsFn: (config: ConversationsRemoteConfig, posthog: PostHog) => LazyLoadedConversationsInterface
    ): void {
        if (!this._remoteConfig) {
            logger.error('Cannot complete initialization: remote config is null')
            return
        }

        try {
            // Pass config and PostHog instance to the extension
            this._conversationsManager = initConversationsFn(this._remoteConfig, this._instance)
            this._loadFailed = false
            logger.info('Conversations loaded successfully')
        } catch (e) {
            this._handleLoadError('Error completing conversations initialization', e)
        }
    }

    /** Helper to handle initialization errors */
    private _handleLoadError(message: string, error?: any): void {
        logger.error(message, error)
        this._conversationsManager = null
        this._isInitializing = false
        this._loadFailed = true
    }

    /**
     * Show the conversations widget (button and chat panel)
     */
    show(): void {
        if (!this._conversationsManager) {
            logger.warn('Conversations not loaded yet.')
            return
        }
        this._conversationsManager.show()
    }

    /**
     * Hide the conversations widget completely (button and chat panel)
     */
    hide(): void {
        if (!this._conversationsManager) {
            return
        }
        this._conversationsManager.hide()
    }

    /**
     * Check if conversations are available (enabled in remote config AND loaded)
     * Use this to check if conversations API can be used.
     */
    isAvailable(): boolean {
        return this._isConversationsEnabled === true && !isNull(this._conversationsManager)
    }

    /**
     * Explain why {@link isAvailable} is currently false, or null when conversations are available.
     * Lets a caller that falls back to another channel record the specific cause (bundle blocked,
     * disabled in the project, remote config not loaded yet, still initializing, …) rather than
     * collapsing every case into a single "unavailable" signal.
     */
    getUnavailableReason(): ConversationsUnavailableReason | null {
        if (this.isAvailable()) {
            return null
        }
        if (this._instance.config.disable_conversations) {
            return 'disabled_by_config'
        }
        // Mirrors loadIfEnabled's toolbar bail: the toolbar's internal instance never owns the
        // conversations manager, so its unavailability is deliberate rather than a failure.
        if (isToolbarInstance(this._instance.config)) {
            return 'disabled_for_toolbar'
        }
        if (this._instance.config.cookieless_mode && this._instance.consent.isOptedOut()) {
            return 'consent_opted_out'
        }
        // Deliberately diverges from loadIfEnabled, which checks __PosthogExtensions__ before remote
        // config. Until remote config settles we do not know conversations was ever meant to load, so
        // reporting a missing extension bundle then would read as an ad blocker during normal startup.
        if (this._remoteConfigSuccessful === false) {
            return 'remote_config_failed'
        }
        if (isUndefined(this._isConversationsEnabled)) {
            return this._remoteConfigSuccessful ? 'disabled_in_project' : 'remote_config_pending'
        }
        if (!this._isConversationsEnabled) {
            return 'disabled_in_project'
        }
        if (isNullish(this._remoteConfig) || !this._remoteConfig.token) {
            return 'missing_token'
        }
        if (!assignableWindow?.__PosthogExtensions__) {
            return 'extensions_unavailable'
        }
        if (this._isInitializing) {
            return 'initializing'
        }
        if (this._loadFailed) {
            return 'load_failed'
        }
        return 'not_loaded'
    }

    /**
     * Check if the widget is currently visible (rendered and shown)
     */
    isVisible(): boolean {
        return this._conversationsManager?.isVisible() ?? false
    }

    /**
     * Send a message programmatically
     * Creates a new ticket if none exists or if newTicket is true
     *
     * @param message - The message text to send
     * @param userTraits - Optional user identification data (name, email)
     * @param newTicket - If true, forces creation of a new ticket (starts new conversation)
     * @returns Promise with response or null if conversations not available yet
     * @note Conversations must be available first (check with isAvailable())
     *
     * @example
     * // Send to existing or create new conversation
     * const response = await posthog.conversations.sendMessage('Hello!', {
     *   name: 'John Doe',
     *   email: 'john@example.com'
     * })
     *
     * @example
     * // Force creation of a new conversation/ticket
     * const newConvo = await posthog.conversations.sendMessage('Start fresh', undefined, true)
     */
    async sendMessage(
        message: string,
        userTraits?: UserProvidedTraits,
        newTicket?: boolean
    ): Promise<SendMessageResponse | null> {
        if (!this._conversationsManager) {
            logger.warn(NOT_AVAILABLE)
            return null
        }
        return this._conversationsManager.sendMessage(message, userTraits, newTicket)
    }

    /**
     * Get messages for the current or specified ticket
     *
     * @param ticketId - Optional ticket ID (defaults to current active ticket)
     * @param after - Optional ISO timestamp to fetch messages after
     * @returns Promise with messages response or null if conversations not available yet
     * @note Conversations must be available first (check with isAvailable())
     *
     * @example
     * // Get messages for current ticket
     * const messages = await posthog.conversations.getMessages()
     *
     * // Get messages for specific ticket
     * const messages = await posthog.conversations.getMessages('ticket-uuid')
     */
    async getMessages(ticketId?: string, after?: string): Promise<GetMessagesResponse | null> {
        if (!this._conversationsManager) {
            logger.warn(NOT_AVAILABLE)
            return null
        }
        return this._conversationsManager.getMessages(ticketId, after)
    }

    /**
     * Mark messages as read for the current or specified ticket
     *
     * @param ticketId - Optional ticket ID (defaults to current active ticket)
     * @returns Promise with response or null if conversations not available yet
     * @note Conversations must be available first (check with isAvailable())
     *
     * @example
     * await posthog.conversations.markAsRead()
     */
    async markAsRead(ticketId?: string): Promise<MarkAsReadResponse | null> {
        if (!this._conversationsManager) {
            logger.warn(NOT_AVAILABLE)
            return null
        }
        return this._conversationsManager.markAsRead(ticketId)
    }

    /**
     * Get list of tickets for the current widget session
     *
     * @param options - Optional filtering and pagination options
     * @returns Promise with tickets response or null if conversations not available yet
     * @note Conversations must be available first (check with isAvailable())
     *
     * @example
     * const tickets = await posthog.conversations.getTickets({
     *   limit: 10,
     *   offset: 0,
     *   status: 'open'
     * })
     */
    async getTickets(options?: GetTicketsOptions): Promise<GetTicketsResponse | null> {
        if (!this._conversationsManager) {
            logger.warn(NOT_AVAILABLE)
            return null
        }
        return this._conversationsManager.getTickets(options)
    }

    /**
     * Request a restore link email for previous conversations.
     *
     * @param email - Email address associated with previous conversations
     * @returns Promise with generic success response or null if conversations unavailable
     */
    async requestRestoreLink(email: string): Promise<RequestRestoreLinkResponse | null> {
        if (!this._conversationsManager) {
            logger.warn(NOT_AVAILABLE)
            return null
        }
        return this._conversationsManager.requestRestoreLink(email)
    }

    /**
     * Redeem a restore token and relink eligible tickets to this browser session.
     *
     * @param restoreToken - Opaque restore token from restore email link
     * @returns Promise with restore status or null if conversations unavailable
     */
    async restoreFromToken(restoreToken: string): Promise<RestoreFromTokenResponse | null> {
        if (!this._conversationsManager) {
            logger.warn(NOT_AVAILABLE)
            return null
        }
        return this._conversationsManager.restoreFromToken(restoreToken)
    }

    /**
     * Parse and redeem `ph_conv_restore` token from the current URL.
     *
     * @returns Promise with restore status, or null when no token/conversations unavailable
     */
    async restoreFromUrlToken(): Promise<RestoreFromTokenResponse | null> {
        if (!this._conversationsManager) {
            logger.warn(NOT_AVAILABLE)
            return null
        }
        return this._conversationsManager.restoreFromUrlToken()
    }

    /**
     * Get the current active ticket ID
     * Returns null if no conversation has been started yet or if not available
     *
     * @returns Ticket ID or null
     * @note Safe to call before conversations are available, will return null
     *
     * @example
     * const ticketId = posthog.conversations.getCurrentTicketId()
     * if (ticketId) {
     *   console.log('Current ticket ID:', ticketId)
     * }
     */
    getCurrentTicketId(): string | null {
        return this._conversationsManager?.getCurrentTicketId() ?? null
    }

    /**
     * Get the widget session ID (persistent browser identifier)
     * This ID is used for access control and stays the same across page loads
     * Returns null if conversations not available yet
     *
     * @returns Session ID or null if not available
     * @note Safe to call before conversations are available, will return null
     *
     * @example
     * const sessionId = posthog.conversations.getWidgetSessionId()
     * if (!sessionId) {
     *   // Conversations not available yet
     *   posthog.conversations.show()
     * }
     */
    getWidgetSessionId(): string | null {
        return this._conversationsManager?.getWidgetSessionId() ?? null
    }

    /** @internal Called by PostHog.setIdentity() -- forwards to the manager without recursing */
    _onIdentityChanged(): void {
        this._conversationsManager?.setIdentity()
    }

    /** @internal Called by PostHog.clearIdentity() -- forwards to the manager without recursing */
    _onIdentityCleared(): void {
        this._conversationsManager?.clearIdentity()
    }
}
